import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage, useIntl } from "react-intl";
import { colors, fontSize } from "style";
import { INTERVIEW_SCHEDULING_REQUEST_STATUS } from "constants/interviewSchedulingRequests";
import Card from "components/Card";
import Status from "components/Status";
import { UserPhoto } from "components/User";
import { BUTTON_SIZE, SecondaryButton } from "components/Buttons";

/**
 * ApplicantInterviewRequestCard
 *
 * One interview request sent to an applicant: status, interviewer, interview time or link, and when it was sent.
 *
 * @param {Object} request Interview scheduling request
 */
const ApplicantInterviewRequestCard = ({ request, ...props }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const [firstName, ...otherNames] = (request.interviewerName || "").split(" ");
  const awaitingBooking = request.status === INTERVIEW_SCHEDULING_REQUEST_STATUS.ready && request.schedulingUrl;

  useEffect(() => {
    if (!copied) return;

    const timeout = setTimeout(() => setCopied(false), COPIED_DURATION);
    return () => clearTimeout(timeout);
  }, [copied]);

  const copyLink = async () => {
    await window.navigator.clipboard.writeText(request.schedulingUrl);
    setCopied(true);
  };

  return (
    <Card noMargin css={styles.card} {...props}>
      <div>
        <Status {...STATUS_TONES[request.status]}>
          <FormattedMessage id={`Missions.Applicants.InterviewModal.Status.${request.status}`} />
        </Status>
      </div>

      <div css={styles.interviewer}>
        <UserPhoto
          css={styles.avatar}
          data={{ firstName, lastName: otherNames.at(-1) || "" }}
          width="3.2rem"
          height="3.2rem"
          borderRadius="100%"
        />
        <span>{request.interviewerName}</span>
      </div>

      {request.scheduledStartAt && (
        <div css={styles.detail}>
          {intl.formatDateTimeRange(
            new Date(request.scheduledStartAt),
            new Date(request.scheduledEndAt || request.scheduledStartAt),
            SCHEDULE_FORMAT
          )}
        </div>
      )}

      {awaitingBooking && request.expiresAt && (
        <div css={styles.detail}>
          <FormattedMessage
            id="Missions.Applicants.InterviewModal.Requests.LinkExpires"
            values={{ date: intl.formatDate(request.expiresAt, { dateStyle: "medium" }) }}
          />
        </div>
      )}

      <div css={[styles.footer, awaitingBooking && styles.divided]}>
        <span css={styles.sent}>{intl.formatDate(request.createdAt, { dateStyle: "medium" })}</span>
        {awaitingBooking && (
          <SecondaryButton type="button" size={BUTTON_SIZE.small} onClick={copyLink}>
            <FormattedMessage id={copied ? "Global.Copied" : "Missions.Applicants.InterviewModal.Requests.CopyLink"} />
          </SecondaryButton>
        )}
      </div>
    </Card>
  );
};

const COPIED_DURATION = 2000;
const SCHEDULE_FORMAT = { dateStyle: "medium", timeStyle: "short" };

const BLUE = { color: colors.lightBlue, background: colors.lightestBlue, dotColor: colors.blueVelvetBase };
const GREEN = { enabled: true };
const RED = { enabled: false };

const STATUS_TONES = {
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.pending]: BLUE,
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.ready]: BLUE,
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.scheduled]: GREEN,
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.completed]: GREEN,
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.canceled]: RED,
  [INTERVIEW_SCHEDULING_REQUEST_STATUS.expired]: RED,
};

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 1.6rem;
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 1rem;
  `,
  avatar: css`
    .initials {
      font-size: ${fontSize.xxsmall};
    }
  `,
  interviewer: css`
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyDark};
  `,
  detail: css`
    color: ${colors.grayAnatomyDark};
  `,
  footer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  `,
  divided: css`
    padding-top: 1.2rem;
    border-top: 1px solid ${colors.grayAnatomyLight3};
  `,
  sent: css`
    color: ${colors.grayAnatomyBase};
  `,
};

ApplicantInterviewRequestCard.propTypes = {
  request: PropTypes.object.isRequired,
};

export default ApplicantInterviewRequestCard;
